/**
 * @fileoverview WEB-710 / WEB-809 — the music spine's lineup module.
 *
 * Maps `event.artists` onto the shared `ModuleRowList` row shape and wraps it
 * in `EventModuleSection`. `EventDetailArtist` carries a real `billing` role
 * and `performanceOrder` (API-1484) — the ordering and the row `meta` are
 * both backed by real data. WEB-809 adds artist imagery and slug-gated
 * `/artist/{slug}` links; two slots this module still never populates (set
 * time `lead`, trailing blurb) stay omitted — see {@link buildLineupRow}.
 *
 * Reuses the billing vocabulary `LineupSection`
 * (`components/organisms/EventDetail/LineupSection.tsx`) already established
 * — `ArtistBilling` values (`'HEADLINER' | 'SUPPORT' | 'OPENER'`) are already
 * upper-case, so they drop straight into the row `meta` with no relabeling —
 * but renders through the shared `ModuleRowList` spine primitive (WEB-707)
 * rather than that organism's own bespoke markup.
 *
 * ## Design spec vs WEB-809
 *
 * `docs/design-system/event-v2-m2-module-specs.md` §1 originally said "No
 * visual" for lineup. WEB-809's AC overrides that: when `Artist.imageUrl` /
 * `imageUrls` is a displayable URL, the row shows a 40px circular photo;
 * when it is not, the visual is **omitted** — never an initials disc or a
 * gradient thumb. Per-artist `title` links to `/artist/{slug}` only when
 * the slug is resolvable; otherwise the name stays plain text (artist pages
 * 404 without a slug — do not fall back to `id`).
 *
 * There is **no dedicated billing-label treatment** — the role is the
 * ordinary row `meta` line, which `ModuleRowList` already renders.
 */

import type { ReactElement } from 'react';

import { EventModuleSection, ModuleRowList } from '@/components/organisms/EventModuleSection';
import type { ModuleRow } from '@/components/organisms/EventModuleSection';
import { readResolvablePageSlug } from '@/lib/eventDetail/readResolvablePageSlug';
import { toDisplayImageUrl } from '@/lib/image-proxy/toDisplayImageUrl';
import type { EventDetailV2 } from '@/types/eventDetail';

import type { EventModuleProps } from './registry';

/** Wire lineup entry — `EventDetailV2['artists']` element after null coalesce. */
type LineupArtist = NonNullable<EventDetailV2['artists']>[number];

/**
 * Coalesce an absent/empty lineup to `[]`. After API-1720 the generated
 * `EventDetailV2['artists']` element type can hold the null description /
 * image / genre the server sends, so no cast is required.
 */
function toLineupArtists(artists: EventDetailV2['artists']): LineupArtist[] {
  return artists ?? [];
}

/**
 * Thumbnail-first image pick (WEB-809 / PIP-235). `imageUrls.thumbnail` is
 * the right size for a 40px avatar; fall through to `medium` then the
 * legacy `imageUrl`. Every candidate runs through {@link toDisplayImageUrl}
 * so unknown hosts collapse to `''` (CSP-safe omit) rather than a broken
 * `<img>`.
 */
function pickArtistImageSrc(artist: LineupArtist): string | null {
  const candidates = [artist.imageUrls?.thumbnail, artist.imageUrls?.medium, artist.imageUrl];
  for (const candidate of candidates) {
    if (typeof candidate !== 'string' || candidate.trim().length === 0) {
      continue;
    }
    const display = toDisplayImageUrl(candidate.trim());
    if (display.length > 0) {
      return display;
    }
  }
  return null;
}

/**
 * One artist → one row. `lead` and `trail` are never set — omitted, not
 * defaulted — matching `ModuleRowList`'s self-gating contract (omit the
 * field, omit the column):
 *
 * - `lead` (set time): `EventDetailArtist` carries no set-time column — LOCI
 *   does not model when an act takes the stage. Reserved for a future
 *   set-time column; the next reader should thread a real value through
 *   here rather than inventing one (design spec §1, §11 degrade-case index).
 * - `trail`: no real per-artist trailing value exists (the mockup's
 *   other-dates / origin-city blurb has no backing column) — omit rather
 *   than guess.
 *
 * `meta` carries the billing role verbatim (already upper-case) and is
 * omitted entirely — not defaulted to `'SUPPORT'` — when `billing` is null.
 *
 * `visual` is a real photo or nothing (WEB-809). `titleHref` is a relative
 * `/artist/{slug}` only when the slug is resolvable — never `/artist/{id}`
 * and never `/artist/artist`.
 */
function buildLineupRow(artist: LineupArtist): ModuleRow {
  const imageSrc = pickArtistImageSrc(artist);
  const slug = readResolvablePageSlug(artist.slug);
  return {
    id: artist.id,
    title: artist.name,
    ...(artist.billing ? { meta: artist.billing } : {}),
    ...(imageSrc !== null ? { visual: { kind: 'image' as const, src: imageSrc } } : {}),
    ...(slug !== null ? { titleHref: `/artist/${encodeURIComponent(slug)}` } : {}),
  };
}

/** `n === 1 ? '1 act' : 'n acts'` — the section meta's count clause. */
function formatActsCount(count: number): string {
  return count === 1 ? '1 act' : `${count} acts`;
}

/**
 * Section meta: `"<n> acts"`, plus the honest `"set times not published"`
 * clause whenever no row carries a set time. Checking `row.lead` (rather than
 * hardcoding the clause) keeps this forward-compatible: because
 * {@link buildLineupRow} never populates `lead` today, this always resolves
 * to the "not published" branch — honestly, not by assertion — and the
 * string updates itself the moment a real set-time source lets a future
 * change populate `lead`.
 */
function buildSectionMeta(rows: readonly ModuleRow[]): string {
  const actsLabel = formatActsCount(rows.length);
  const anySetTimePublished = rows.some((row) => Boolean(row.lead));
  return anySetTimePublished ? actsLabel : `${actsLabel} · set times not published`;
}

/**
 * Orders the lineup: `performanceOrder` ascending, **nulls last**, then name.
 *
 * The API already returns this order, and this file's own header claims the
 * rows render in `performanceOrder` — but claiming an order while mapping the
 * payload verbatim makes the guarantee true only for as long as every producer
 * happens to cooperate. One unsorted response and the acts render in the wrong
 * order silently, with a doc comment insisting otherwise. Re-applying it here
 * costs an O(n log n) pass over a handful of artists and makes the documented
 * contract enforceable rather than aspirational.
 *
 * Nulls sort last rather than first: an artist with no declared slot is not
 * the headliner, and defaulting a missing order to 0 would promote it above
 * acts that *do* declare one. Ties fall back to `localeCompare` so the result
 * is total — `Array.prototype.sort` is stable in modern engines, but relying
 * on that would make the order depend on payload order for ties, which is the
 * thing this function exists to stop depending on.
 *
 * Copies before sorting; `event.artists` is caller-owned and must not be
 * mutated by a render.
 */
function sortLineupArtists(artists: readonly LineupArtist[]): LineupArtist[] {
  return [...artists].sort((a, b) => {
    const aOrder = typeof a.performanceOrder === 'number' ? a.performanceOrder : null;
    const bOrder = typeof b.performanceOrder === 'number' ? b.performanceOrder : null;

    if (aOrder !== bOrder) {
      if (aOrder === null) return 1;
      if (bOrder === null) return -1;
      return aOrder - bOrder;
    }

    return (a.name ?? '').localeCompare(b.name ?? '');
  });
}

/**
 * `LineupModule` — the music spine's `lineup` kind. `module.hasContent` is
 * authoritative (`resolveEventModules` already gates it on
 * `event.artists.length > 0`); this component never re-derives that check
 * from `event` itself, and renders nothing when it is false — WEB-719 (M4)
 * owns the degrade note, not this file.
 */
export function LineupModule({ module, event, chroma }: EventModuleProps): ReactElement | null {
  if (!module.hasContent) {
    return null;
  }

  const rows = sortLineupArtists(toLineupArtists(event.artists)).map(buildLineupRow);
  const sectionModule = { ...module, meta: buildSectionMeta(rows) };

  return (
    <EventModuleSection module={sectionModule} chroma={chroma}>
      <ModuleRowList rows={rows} chroma={chroma} />
    </EventModuleSection>
  );
}
